import { Injectable, OnDestroy } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarDismiss,
} from '@angular/material/snack-bar';
import {
  BehaviorSubject,
  delay,
  filter,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
  tap,
} from 'rxjs';

export interface SnackbarConfigParams extends MatSnackBarConfig {
  triggerAction?: any;
}

export interface SnackBarQueueItem {
  message: string;
  beingDispatched: boolean;
  configParams?: SnackbarConfigParams;
}

/**
 * @deprecated Use libs/core/src/lib/utils/snackbar.service.ts
 */
@Injectable({ providedIn: 'root' })
export class ToastService implements OnDestroy {
  private readonly snackBarQueue = new BehaviorSubject<SnackBarQueueItem[]>([]);
  private readonly snackBarQueue$ = this.snackBarQueue.asObservable();
  private readonly ngDestroy = new Subject();
  defaultAction = 'Close';

  constructor(private _snackBar: MatSnackBar) {
    /* Dispatches all queued snack bars one by one */
    this.snackBarQueue$
      .pipe(
        filter((queue) => queue.length > 0 && !queue[0].beingDispatched),
        tap(() => {
          const updatedQueue = this.snackBarQueue.value;
          updatedQueue[0].beingDispatched = true;
          this.snackBarQueue.next(updatedQueue);
        }),
        map((queue) => queue[0]),
        takeUntil(this.ngDestroy),
      )
      .subscribe((snackBarItem) =>
        this.showSnackbar(snackBarItem.message, snackBarItem.configParams),
      );
  }

  public ngOnDestroy() {
    this.snackBarQueue.next([]);
    this.snackBarQueue.complete();
    this.ngDestroy.next(null);
    this.ngDestroy.complete();
  }

  public queueSnackBar(message: string, configParams?: SnackbarConfigParams) {
    this.snackBarQueue.next(
      this.snackBarQueue.value.concat([
        { message, configParams, beingDispatched: false },
      ]),
    );
  }

  private showSnackbar(message: string, configParams?: SnackbarConfigParams) {
    const duration = this.getDuration(configParams);
    const triggerAction = configParams?.triggerAction || this.defaultAction;
    this.removeDismissedSnackBar(
      this._snackBar
        .open(message, triggerAction, { duration })
        .afterDismissed(),
    );
    this.triggerAction(configParams);
  }

  /* Remove dismissed snack bar from queue and triggers another one to appear */
  private removeDismissedSnackBar(dismissed: Observable<MatSnackBarDismiss>) {
    dismissed
      .pipe(
        delay(500), // delay 0.5s
        take(1),
      )
      .subscribe(() => {
        const updatedQueue = this.snackBarQueue.value;
        if (updatedQueue[0].beingDispatched) updatedQueue.shift();
        this.snackBarQueue.next(updatedQueue);
      });
  }

  private getDuration(configParams?: SnackbarConfigParams): number {
    if (configParams && configParams.duration) return configParams.duration;
    else return 3000;
  }

  /* In case you would like to dispatch actions in nxrx when snack bar is shown, etc */
  private triggerAction(configParams?: SnackbarConfigParams) {
    if (configParams && configParams.triggerAction) {
      // TODO: integrate actions with ngrx store
      // if (configParams.triggerAction === 'FREE_LIMIT_WARNING_SHOWN') {
      // this.store.dispatch(new FreeLimitWarningShown());
      // }
    }
  }

  private calculateDuration(message: string): number {
    if (!message) {
      // report bug for console
      throw new Error('Message is required');
    }
    const numCharacters = 10;
    const baseDuration = 1000; // 1 second per numCharacters

    const messageLength = message.length;
    return Math.ceil(messageLength / numCharacters) * baseDuration;
  }

  openSnackBar(
    message: string,
    config?: (Partial<MatSnackBarConfig> & { action: string }) | string | any,
  ) {
    let toastAction;
    let options: Partial<MatSnackBarConfig> = {
      duration: this.calculateDuration(message),
      verticalPosition: 'top',
      panelClass: 'custom-snackbar', // by default panel class
    };
    if (typeof config === 'string') {
      toastAction = config;
    }
    if (typeof config === 'object') {
      const { action, ...otherConfig } = config;
      if (action) {
        toastAction = config.action;
      }
      options = { ...options, ...otherConfig };
    }
    toastAction = toastAction || this.defaultAction;
    return this._snackBar.open(message, toastAction, options);
  }

  closeAll() {
    this._snackBar.dismiss();
  }
  openLateralSnackBar(
    message: string,
    config?: (Partial<MatSnackBarConfig> & { action: string }) | string | any,
  ) {
    let toastAction;
    let options: Partial<MatSnackBarConfig> = {
      duration: this.calculateDuration(message),
      verticalPosition: 'bottom',
      horizontalPosition: 'right',
      panelClass: 'custom-snackbar', // by default panel class
    };
    if (typeof config === 'string') {
      toastAction = config;
    }
    if (typeof config === 'object') {
      const { action, ...otherConfig } = config;
      if (action) {
        toastAction = config.action;
      }
      options = { ...options, ...otherConfig };
    }
    toastAction = toastAction || this.defaultAction;
    return this._snackBar.open(message, toastAction, options);
  }
}
